import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

interface AnalyticsData {
  totalSales: number;
  totalOrders: number;
  avgOrderValue: number;
  productCount: number;
  salesData: Array<{ month: string; sales: number; orders: number }>;
  productData: Array<{ name: string; sales: number }>;
}

export default function SellerAnalytics() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { user } = useAuth();
  
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalSales: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    productCount: 0,
    salesData: [],
    productData: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadAnalyticsData();
    }
  }, [user]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        throw new Error('사용자 정보를 찾을 수 없습니다.');
      }

      // 1. seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        console.error('Seller Profile 정보를 찾을 수 없습니다:', profileError);
        throw new Error('판매자 프로필을 찾을 수 없습니다.');
      }

      // 2. 판매자의 상품 조회 (seller_profile.id 사용)
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('product_id, product_name')
        .eq('seller_id', sellerProfile.id);

      if (productsError) throw productsError;

      const productIds = products?.map(p => p.product_id) || [];
      
      if (productIds.length === 0) {
        // 상품이 없으면 빈 데이터 설정
        setAnalyticsData({
          totalSales: 0,
          totalOrders: 0,
          avgOrderValue: 0,
          productCount: 0,
          salesData: [],
          productData: []
        });
        return;
      }

      // 3. 해당 상품들의 주문 아이템 조회
      const { data: orderItems, error: orderItemsError } = await supabase
        .from('order_items')
        .select(`
          *,
          products!inner (
            product_id,
            product_name,
            seller_id
          ),
          orders!inner (
            id,
            created_at,
            delivery_status
          )
        `)
        .in('product_id', productIds)
        .eq('products.seller_id', sellerProfile.id);

      if (orderItemsError) throw orderItemsError;

      console.log('Order Items for Seller:', orderItems);

      // 3. 통계 계산
      const totalSales = orderItems?.reduce((sum, item) => sum + (item.total_price || 0), 0) || 0;
      const totalOrders = orderItems?.length || 0;
      const avgOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
      const productCount = products?.length || 0;

      // 4. 월별 매출 데이터 생성
      const monthlyData: { [key: string]: { sales: number; orders: number } } = {};
      
      orderItems?.forEach(item => {
        const date = new Date(item.orders.created_at);
        const monthKey = date.toLocaleString('default', { month: 'short' });
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { sales: 0, orders: 0 };
        }
        
        monthlyData[monthKey].sales += item.total_price || 0;
        monthlyData[monthKey].orders += 1;
      });

      const salesData = Object.entries(monthlyData).map(([month, data]) => ({
        month,
        sales: data.sales,
        orders: data.orders
      }));

      // 5. 상품별 매출 데이터 생성
      const productSales: { [key: string]: number } = {};
      
      orderItems?.forEach(item => {
        const productName = item.products?.product_name || '알 수 없는 상품';
        productSales[productName] = (productSales[productName] || 0) + (item.total_price || 0);
      });

      const productData = Object.entries(productSales)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 4)
        .map(([name, sales]) => ({ name, sales }));

      setAnalyticsData({
        totalSales,
        totalOrders,
        avgOrderValue,
        productCount,
        salesData,
        productData
      });

    } catch (error) {
      console.error('Analytics data loading error:', error);
      setError(error instanceof Error ? error.message : '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  const stats = [
    { label: 'Total Sales', value: formatCurrency(analyticsData.totalSales) },
    { label: 'Total Orders', value: analyticsData.totalOrders.toString() },
    { label: 'Avg. Order Value', value: formatCurrency(Math.round(analyticsData.avgOrderValue)) },
    { label: 'Products', value: analyticsData.productCount.toString() },
  ];

  if (loading) {
    return (
      <Box sx={styles.pageContainer}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.pageContainer}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight={700} mb={3} sx={styles.title}>
        Analytics
      </Typography>
      <Grid container spacing={3} mb={2}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <Card sx={{ ...styles.paper, p: 2 }}>
              <CardContent>
                <Typography color={styles.text.secondary} gutterBottom fontWeight={600}>
                  {stat.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} color={styles.text.primary}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ ...styles.paper, p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2} color={styles.text.primary}>
              Sales & Orders Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#404040' : '#f0f0f0'} />
                <XAxis dataKey="month" stroke={styles.text.secondary} />
                <YAxis yAxisId="left" stroke={styles.text.secondary} />
                <YAxis yAxisId="right" orientation="right" stroke={styles.text.secondary} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#404040' : '#fff', 
                    border: `1px solid ${styles.table.cell.borderColor}`,
                    borderRadius: '8px',
                    color: styles.text.primary
                  }}
                  formatter={(value, name) => [
                    name === 'sales' ? formatCurrency(Number(value)) : value,
                    name === 'sales' ? 'Sales' : 'Orders'
                  ]}
                />
                <Line yAxisId="left" type="monotone" dataKey="sales" stroke={isDarkMode ? '#fff' : '#000'} name="sales" />
                <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#82ca9d" name="orders" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...styles.paper, p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2} color={styles.text.primary}>
              Sales by Product
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.productData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="sales"
                >
                  {analyticsData.productData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#404040' : '#fff', 
                    border: `1px solid ${styles.table.cell.borderColor}`,
                    borderRadius: '8px',
                    color: styles.text.primary
                  }}
                  formatter={(value) => [formatCurrency(Number(value)), 'Sales']}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
} 